import { AdvancedBooleanExpressionInterface } from '../../../api/mentions/model/advanced-boolean-expression.interface';
import { SearchContextModel } from './search-context.model';
import { SearchContextsEnum } from '../enums/search-contexts.enum';
import { searchContextFromApiMap } from './search-contexts.map';
import { MonitoringKeywordGroupModel } from './monitoring-keyword-group.model';
import { allSearchContexts } from './monitoring-keyword-filter.model';

enum QueryItemEnum {
    term,
    operator,
}
export class MonitoringAdvancedBooleanModel {
    static maxLength: number = 3072;
    static minLength: number = 3;
    searchContexts: SearchContextModel[] = [];
    filter: string;
    queryValidationError: string;

    constructor(apiModel?: AdvancedBooleanExpressionInterface) {
        if (apiModel) {
            this.constructFromApi(apiModel);
        } else {
            this.constructDefault();
        }
    }

    constructFromApi(apiModel: AdvancedBooleanExpressionInterface) {
        this.filter = apiModel.filter;
        this.searchContexts = apiModel.areas.map(
            area => new SearchContextModel(searchContextFromApiMap.get(area))
        );
    }

    constructDefault() {
        this.searchContexts = [
            new SearchContextModel(SearchContextsEnum.header),
            new SearchContextModel(SearchContextsEnum.lead),
            new SearchContextModel(SearchContextsEnum.body)
        ];
        this.filter = '';
    }

    equals(expertBooleanQuery: MonitoringAdvancedBooleanModel): boolean {
        if (!expertBooleanQuery) {
            return false;
        }

        if (this.filter.length !== expertBooleanQuery.filter.length ||
            this.filter !== expertBooleanQuery.filter
        ) {
            return false;
        }

        // Easy check - if length different then we aren't the same
        if (this.searchContexts.length !== expertBooleanQuery.searchContexts.length) {
            return false;
        }

        // Otherwise we do an in depth check, looking at which elements are enabled or not.
        let found: boolean;
        const haystackLength = this.searchContexts.length;
        const needleLength = expertBooleanQuery.searchContexts.length;
        for (let i = 0; i < haystackLength; i++) {
            found = false;
            for (let j = 0; j < needleLength; j++) {
                if (this.searchContexts[i].value === expertBooleanQuery.searchContexts[j].value) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                return false;
            }
        }

        return true;
    }

    /**
     * Converts a set of keyword groups to boolean query format
     *
     * @param keywordGroups A set of keyword groups
     * @param forceQuotes Toggle to force wrapping each keyword/phrase in quotes, even if not required (This is to align with backend when validating)
     */
    mapKeywordGroupQueryToExpertBooleanQuery(keywordGroups: MonitoringKeywordGroupModel[], forceQuotes: boolean = false): void {

        keywordGroups = keywordGroups.filter(x => x.and?.length || x.or?.length || x.not?.length);

        if (keywordGroups.length === 0) {
            return;
        } else if (keywordGroups.length === 1) {
            this.filter = keywordGroups[0].exportKeywordGroupPhrasesToString();
            this.searchContexts = keywordGroups[0].searchContexts;
        } else {
            this.filter = '(' + keywordGroups.map(group => group.exportKeywordGroupPhrasesToString(forceQuotes)).join(') AND (') + ')';
            this.searchContexts = [...allSearchContexts].map(x => new SearchContextModel(x));
        }
    }

    /**
     * AI Generated conversion from expert to keyword
     */
    /**
     * Attempts to convert this advanced boolean expression into one or more MonitoringKeywordGroupModels.
     * Returns null if the expression is too complex to represent as keyword groups.
     *
     * Single-group filters (e.g. `(a AND b) AND (c OR d) AND NOT (e)`) produce a one-element array.
     * Multi-group filters produced by mapKeywordGroupQueryToExpertBooleanQuery (e.g. `((a) AND (b)) AND ((c OR d))`)
     * produce multiple elements.
     */
    tryConvertToKeywordGroups(): MonitoringKeywordGroupModel[] | null {
        const filter = this.filter?.trim() ?? '';

        if (!filter) {
            const group = new MonitoringKeywordGroupModel();
            group.searchContexts = this.searchContexts.slice();
            return [group];
        }

        if (/\\["\(\)]/.test(filter)) {
            return null;
        }

        const segments = this.splitTopLevelSegments(filter);
        if (!segments) {
            return null;
        }

        // Multi-group format: every top-level segment is an outer paren wrapping a complete
        // single-group filter, so each inner content contains its own parens.
        const hasNestedParens = segments.some(({ content }) => {
            if (!content.startsWith('(') || !content.endsWith(')')) {
                return false;
            }
            const inner = content.slice(1, -1);
            return inner.includes('(') || inner.includes(')');
        });

        if (hasNestedParens) {
            // Multi-group: mapKeywordGroupQueryToExpertBooleanQuery only uses AND between groups.
            if (segments.some(s => s.prefix === 'AND NOT')) {
                return null;
            }

            const groups: MonitoringKeywordGroupModel[] = [];
            for (const { content } of segments) {
                if (!content.startsWith('(') || !content.endsWith(')')) {
                    return null;
                }
                const group = this.parseSingleKeywordGroupFromFilter(content.slice(1, -1).trim());
                if (!group) {
                    return null;
                }
                groups.push(group);
            }

            return groups;
        }

        // Single-group
        const group = this.parseSingleKeywordGroupFromSegments(segments);
        if (!group) {
            return null;
        }

        return [group];
    }

    private parseSingleKeywordGroupFromFilter(filter: string): MonitoringKeywordGroupModel | null {
        if (/\\["\(\)]/.test(filter)) {
            return null;
        }
        const segments = this.splitTopLevelSegments(filter);
        if (!segments) {
            return null;
        }
        if (segments.some(({ content }) => {
            if (!content.startsWith('(') || !content.endsWith(')')) {
                return false;
            }
            const inner = content.slice(1, -1);
            return inner.includes('(') || inner.includes(')');
        })) {
            return null;
        };
        return this.parseSingleKeywordGroupFromSegments(segments);
    }

    private parseSingleKeywordGroupFromSegments(segments: Array<{ prefix: string; content: string }>): MonitoringKeywordGroupModel | null {
        const notSegments = segments.filter(s => s.prefix === 'AND NOT');
        const nonNotSegments = segments.filter(s => s.prefix !== 'AND NOT');

        const andTerms: string[] = [];
        const orTerms: string[] = [];
        const notTerms: string[] = [];

        // Merge all NOT blocks: AND NOT (a) AND NOT (b) === AND NOT (a OR b)
        for (const { content } of notSegments) {
            const inner = this.extractGroupInner(content);
            if (!inner) {
                return null;
            }
            const parsed = this.parseGroupTerms(inner);
            if (!parsed || parsed.operator === 'AND') {
                return null;
            }
            notTerms.push(...parsed.terms);
        }

        // Merge non-NOT blocks: AND blocks and SINGLE blocks fold into andTerms,
        // at most one OR block is allowed (multiple OR blocks can't be represented).
        const orBlocks: string[][] = [];
        for (const { content } of nonNotSegments) {
            const inner = this.extractGroupInner(content);
            if (!inner) {
                return null;
            }
            const parsed = this.parseGroupTerms(inner);
            if (!parsed) {
                return null;
            }
            if (parsed.operator === 'OR') {
                orBlocks.push(parsed.terms);
            } else {
                andTerms.push(...parsed.terms);
            }
        }

        if (orBlocks.length > 1) {
            return null;
        }
        if (orBlocks.length === 1) {
            orTerms.push(...orBlocks[0]);
        }

        const group = new MonitoringKeywordGroupModel();
        group.and = andTerms;
        group.or = orTerms;
        group.not = notTerms;
        group.searchContexts = this.searchContexts.slice();

        return group;
    }

    private extractGroupInner(content: string): string | null {
        if (!content.startsWith('(') || !content.endsWith(')')) {
            return null;
        }
        const inner = content.slice(1, -1);
        if (inner.includes('(') || inner.includes(')')) {
            return null;
        }
        return inner;
    }

    private splitTopLevelSegments(filter: string): Array<{ prefix: string; content: string }> | null {
        const segments: Array<{ prefix: string; content: string }> = [];
        let depth = 0;
        let inQuote = false;
        let segmentStart = 0;
        let currentPrefix = '';
        let i = 0;

        while (i < filter.length) {
            const ch = filter[i];
            if (ch === '"') {
                inQuote = !inQuote;
            } else if (!inQuote) {
                if (ch === '(') {
                    depth++;
                } else if (ch === ')') {
                    depth--;
                } else if (depth === 0) {
                    if (filter.startsWith(' AND NOT ', i)) {
                        segments.push({ prefix: currentPrefix, content: filter.substring(segmentStart, i).trim() });
                        currentPrefix = 'AND NOT';
                        i += ' AND NOT '.length;
                        segmentStart = i;
                        continue;
                    } else if (filter.startsWith(' AND ', i)) {
                        segments.push({ prefix: currentPrefix, content: filter.substring(segmentStart, i).trim() });
                        currentPrefix = 'AND';
                        i += ' AND '.length;
                        segmentStart = i;
                        continue;
                    } else if (filter.startsWith(' OR ', i)) {
                        return null;
                    }
                }
            }
            i++;
        }

        const lastContent = filter.substring(segmentStart).trim();
        if (lastContent) {
            segments.push({ prefix: currentPrefix, content: lastContent });
        }

        return segments.length > 0 ? segments : null;
    }

    private parseGroupTerms(inner: string): { terms: string[]; operator: 'AND' | 'OR' | 'SINGLE' } | null {
        const terms: string[] = [];
        let detectedOperator: 'AND' | 'OR' | 'SINGLE' = 'SINGLE';
        const reservedOperators = new Set(['AND', 'OR', 'NOT']);
        let i = 0;

        while (i < inner.length) {
            while (i < inner.length && inner[i] === ' ') {
                i++;
            }
            if (i >= inner.length) {
                break;
            }

            let term: string;
            if (inner[i] === '"') {
                const closeIdx = inner.indexOf('"', i + 1);
                if (closeIdx === -1) {
                    return null;
                }
                const phraseContent = inner.substring(i + 1, closeIdx);
                i = closeIdx + 1;
                const proximityMatch = inner.substring(i).match(/^~(\d+)/);
                if (proximityMatch && parseInt(proximityMatch[1]) > 0) {
                    term = `"${phraseContent}"${proximityMatch[0]}`;
                    i += proximityMatch[0].length;
                } else {
                    term = phraseContent;
                }
            } else {
                let end = i;
                while (end < inner.length && inner[end] !== ' ') {
                    end++;
                }
                term = inner.substring(i, end);
                i = end;
                if (reservedOperators.has(term)) {
                    return null;
                }
            }

            if (!term) {
                return null;
            }
            terms.push(term);

            while (i < inner.length && inner[i] === ' ') {
                i++;
            }
            if (i >= inner.length) {
                break;
            }

            if (inner.startsWith('AND NOT ', i)) {
                return null;
            } else if (inner.startsWith('AND ', i)) {
                if (detectedOperator === 'OR') {
                    return null;
                }
                detectedOperator = 'AND';
                i += 'AND '.length;
            } else if (inner.startsWith('OR ', i)) {
                if (detectedOperator === 'AND') {
                    return null;
                }
                detectedOperator = 'OR';
                i += 'OR '.length;
            } else {
                return null;
            }
        }

        if (terms.length === 0) {
            return null;
        }
        if (terms.length === 1) {
            detectedOperator = 'SINGLE';
        }

        return { terms, operator: detectedOperator };
    }

    /**
     * Translates keywordGroups to ANDed string
     */
    convertBasicQueryToExpert(keywordGroups: MonitoringKeywordGroupModel[], forceQuotes: boolean = false): string {
        const validKeywords = keywordGroups.filter(keywordGroup => keywordGroup.isAnyKeywordSet());
        if (validKeywords.length === 0) {
            return '';
        }

        const keywordsConverted = validKeywords.reduce((accumulator, currentValue, index) => {
            let value = currentValue.exportKeywordGroupPhrasesToString();
            if (index !== validKeywords.length-1) {
                value += ' AND ';
            }
            return accumulator + value;
        },
        '');

        return keywordsConverted;
    }


    /**
     * Checks to see if quotes and parenthesis usage in the query is balanced
     */
    public queryStructureIsValid(): boolean {
        const validationStack = [];
        let lastItemOnStack = [];
        this.queryValidationError = '';

        // Escaped double quotes and open/close parenthesis are treated as plain characters, not
        // part of the query structure. "?" here is used as a placeholder for validation only
        let cleansedString = this.filter.replace(/\\"/g, '?');
        cleansedString = cleansedString.replace(/\\\(/g, '?');
        cleansedString = cleansedString.replace(/\\\)/g, '?');

        const closingQuoteIndexes: number[] = [];
        const validationRegex = /[(")]/g;

        let match: RegExpExecArray;
        while ((match = validationRegex.exec(cleansedString))) {
            const currentChar = match[0];
            if (validationStack.length === 0) {
                switch (currentChar) {
                    case '(':
                    case '"':
                        validationStack.push(currentChar);
                        break;

                    case ')':
                        this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.missingOpeningParenthesis';
                        return false;
                }
            } else {
                // We remove the last item we added for comparison - if things are good it remains off the stack
                // It might get re-added to the stack if the current item has no issues following this item
                lastItemOnStack = validationStack.splice(validationStack.length - 1, 1);
                switch (currentChar) {
                    case '(':
                        switch (lastItemOnStack[0]) {
                            case '(':
                                validationStack.push(lastItemOnStack[0]);
                                validationStack.push(currentChar);
                                break;

                            case ')':
                                break;

                            case '"':
                                // We are within a quote so we don't worry about including this in balancing
                                validationStack.push(lastItemOnStack[0]);
                                break;
                        }
                        break;

                    case '"':
                        closingQuoteIndexes.push(match.index);
                        switch (lastItemOnStack[0]) {
                            case '(':
                                validationStack.push(lastItemOnStack[0]);
                                validationStack.push(currentChar);
                                break;

                            case ')':
                                this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.missingClosingQuote';
                                return false;

                            case '"':
                                break;
                        }
                        break;

                    case ')':
                        switch (lastItemOnStack[0]) {
                            case '(':
                                break;

                            case '"':
                                // We are within a quote so we don't worry about including this in balancing
                                validationStack.push(lastItemOnStack[0]);
                                break;

                            case ')':
                                this.queryValidationError =
                                    'Monitoring.ExpertBooleanSearch.Error.tooManyClosingParenthesis';
                                return false;
                        }
                        break;
                }
            }
        }

        // If we have anything left in the stack, we are unbalanced and an invalid query
        if (validationStack.length > 0) {
            switch (validationStack[0]) {
                case '(':
                    this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.missingClosingParenthesis';
                    break;

                case '"':
                    this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.missingClosingQuote';
                    break;

                case ')':
                    this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.tooManyClosingParenthesis';
                    break;
            }
            return false;
        }

        const proximity = /(\S?)(\s*)~([^\)\s]*)/g;

        while ((match = proximity.exec(cleansedString))) {
            if (match[1] !== '"') {
                // must be preceded by a double-quote
                this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.proximityMustFollowQuote';
                return false;
            } else if (!closingQuoteIndexes.includes(match.index)) {
                // this is an opening quote, so don't validate what's inside the quoted area
                continue;
            }
            if(match[2]) {
                this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.proximityImmediatelyFollowQuote';
                return false;
            }
            if (!match[3] || !Number.isInteger(+match[3]) || +match[3] <= 0) {
                // must be followed by a number
                this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.proximityMustBeFollowedByNumber';
                return false;
            }
        }

        if(!this.isValidTerms(cleansedString)) {
            return false;
        }

        return true;
    }

    public hasProximityOperator(): boolean {
        return this.filter.includes('~');
    }

    public isQueryTooLong(): boolean {
        return this.filter.length > MonitoringAdvancedBooleanModel.maxLength;
    }

    public isQueryTooShort(): boolean {
        return this.filter.length < MonitoringAdvancedBooleanModel.minLength && this.filter.length > 0;
    }

    public isPureNot(): boolean {
        return this.filter.trim().startsWith('AND NOT');
    }

    public isValidTerms(cleansedString: String): boolean {
        // format query, so each term and oprator are separated by a white space, and unnecessary spaces are removed
        const query = cleansedString
            .replace(/\(/g, ' ( ')
            .replace(/\)/g, ' ) ')
            .replace(/"/g, ' " ')
            .replace(/\s+/g, ' ')
            .replace(/(?<=\s)AND\s*NOT(?=\s)/g, 'ANDNOT')
            .trim();

        const queryStack = query.split(' ');
        const termStack: QueryItemEnum[] = [];
        const operatorStack: string[] = [];
        const booleanOperatorPattern = /^(AND|OR|ANDNOT)$/;
        const operatorPattern = /^[(")]$/;
        const proximityPattern = /^~\d+$/;

        for(let i = 0; i < queryStack.length; i++){
            const currentItem = queryStack[i];

            if(operatorStack[operatorStack.length-1] === '"' && currentItem !== '"') {
                continue;
            }

            if(proximityPattern.test(currentItem)){ // "~#" after '"'
                continue;
            }

            if(operatorPattern.test(currentItem)) { // '(', ')' or '"'
                if(currentItem === '"' && operatorStack[operatorStack.length-1] === '"') {
                    if(termStack[termStack.length-1] === QueryItemEnum.term) { // multi-word term
                        this.queryValidationError = 'Monitoring.ExpertBooleanSearch.help.point1';
                        return false;
                    }
                    termStack.push(QueryItemEnum.term);
                    operatorStack.pop();
                } else if (currentItem === ')') {
                    operatorStack.pop();
                } else {
                    operatorStack.push(currentItem);
                }
                continue;
            }

            if(booleanOperatorPattern.test(currentItem)) { // boolean operator
                if(i === 0 || i === queryStack.length - 1) {
                    this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.startOrEndWithBooleanOperator';
                    return false;
                }
                if(termStack[termStack.length-1] === QueryItemEnum.operator || queryStack[i-1] === '(' || queryStack[i+1] === ')'){
                    this.queryValidationError = 'Monitoring.ExpertBooleanSearch.Error.consecutiveOperators';
                    return false;
                }
                termStack.push(QueryItemEnum.operator);
            } else { // standalone term
                if(termStack[termStack.length-1] === QueryItemEnum.term) {
                    this.queryValidationError = 'Monitoring.ExpertBooleanSearch.help.point1';
                    return false;
                }
                termStack.push(QueryItemEnum.term);
            }

        }
        return true;
    }

    clone(): MonitoringAdvancedBooleanModel {
        const clone = new MonitoringAdvancedBooleanModel();
        clone.filter = this.filter;
        clone.searchContexts = this.searchContexts.slice();
        clone.queryValidationError = this.queryValidationError;

        return clone;
    }
}
